import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiRefreshCw, FiClock } from 'react-icons/fi';
const getToken = () => localStorage.getItem('toolboxToken') || '';

interface CredentialRow {
  transactionId: string;
  name: string;
  apiKey: string;
  secretKey: string;
  agents: string;
}

const LinqsESignatureHistory: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows]       = useState<CredentialRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch]   = useState('');
  const [error, setError]     = useState('');

  const fetchHistory = () => {
    setLoading(true);
    setError('');
    axios.get(`${(process.env.REACT_APP_DataFabric_Url || '')}/api/RabbitSign/history`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((res) => {
        const raw = res.data.responseJson ?? [];
        setRows(raw.map((item: any) => {
          const agents = JSON.parse(item.agents ?? '[]');
          return {
            transactionId: item.transactionId,
            name: item.name,
            apiKey: item.apiKey,
            secretKey: item.secretKey,
            agents: agents.map((a: any) => a.name).join(', '),
          };
        }));
      })
      .catch((err) => {
        if (err.response?.status === 401) navigate('/');
        else setError('Unable to load credential history. Please try again.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchHistory(); }, []);

  const filtered = search.trim()
    ? rows.filter((r) =>
        r.name.toLowerCase().includes(search.toLowerCase()) ||
        r.agents.toLowerCase().includes(search.toLowerCase()),
      )
    : rows;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Toolbar ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
          <FiSearch size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or agents…"
            style={{
              width: '100%',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 8,
              padding: '7px 10px 7px 32px',
              color: 'var(--text-primary)',
              fontSize: 13,
              fontFamily: 'inherit',
              outline: 'none',
            }}
          />
        </div>
        <button
          onClick={fetchHistory}
          disabled={loading}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 8, padding: '7px 14px',
            color: 'var(--text-secondary)', fontSize: 13,
            cursor: 'pointer', fontFamily: 'inherit',
            opacity: loading ? 0.6 : 1,
          }}
        >
          <FiRefreshCw size={13} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          Refresh
        </button>
      </div>

      {/* ── Error ── */}
      {error && (
        <div style={{
          padding: '10px 14px', borderRadius: 8,
          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
          color: '#ef4444', fontSize: 13,
        }}>
          {error}
        </div>
      )}

      {/* ── Table ── */}
      <div style={{
        background: 'var(--surface-glass)',
        border: '1px solid var(--border-glass)',
        borderRadius: 12, overflow: 'hidden',
      }}>
        {loading ? (
          <div className="qp-history-loader" style={{ minHeight: 280 }}>
            <div className="qp-history-loader__orb">
              <div className="qp-history-loader__ring qp-history-loader__ring--1" />
              <div className="qp-history-loader__ring qp-history-loader__ring--2" />
              <div className="qp-history-loader__core"><FiClock size={16} /></div>
            </div>
            <div className="qp-history-loader__text">
              <p className="qp-history-loader__title">Loading History</p>
              <p className="qp-history-loader__sub">
                Fetching e-signature records
                <span className="qp-init-loader__dots"><span>.</span><span>.</span><span>.</span></span>
              </p>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13, padding: '40px 0' }}>
            {search ? 'No results match your search.' : 'No credentials stored yet.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                  {['Key Name', 'API Key', 'Secret Key', 'Assigned Agents'].map((h) => (
                    <th key={h} style={{
                      color: 'var(--text-secondary)', fontWeight: 600,
                      fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em',
                      padding: '10px 16px', textAlign: 'left',
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr
                    key={row.transactionId}
                    style={{
                      borderBottom: '1px solid rgba(255,255,255,0.04)',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.025)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={{ padding: '12px 16px', color: 'var(--text-primary)', fontWeight: 500 }}>{row.name}</td>
                    <td style={{ padding: '12px 16px', color: 'var(--text-secondary)', fontFamily: 'monospace', fontSize: 12 }}>
                      {row.apiKey.length > 24 ? `${row.apiKey.slice(0, 24)}…` : row.apiKey}
                    </td>
                    <td style={{ padding: '12px 16px', color: 'var(--text-secondary)', fontFamily: 'monospace', fontSize: 12 }}>
                      {row.secretKey.length > 24 ? `${row.secretKey.slice(0, 24)}…` : row.secretKey}
                    </td>
                    <td style={{ padding: '12px 16px', color: 'var(--text-secondary)' }}>{row.agents || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ color: 'var(--text-secondary)', fontSize: 12, textAlign: 'right' }}>
        {filtered.length} credential{filtered.length !== 1 ? 's' : ''}
        {search && rows.length !== filtered.length ? ` (filtered from ${rows.length})` : ''}
      </div>
    </div>
  );
};

export default LinqsESignatureHistory;

